/**
 * Hierarchy Search — Lab Aggregate `#!/hierarchy-search` in Strix People rail.
 *
 * Tag usernames → Player / Master / SM / DM / TM chain (memberList scope).
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Banner,
  Button,
  Card,
  DataTable,
  Icon,
  PageHead,
  Pager,
  Spinner,
  StateBlock,
  type Column,
} from '@w7/ui'
import { useSearchHierarchyNew } from '@/api/hooks/member/useSearchHierarchyNew'
import { useSearchHierarchyPage } from '@/api/hooks/member/useSearchHierarchyPage'
import {
  HIERARCHY_PAGE_SIZE,
  hierarchyColumnsForAggregate,
  hierarchySearchLive,
  hierarchySearchSuggestions,
  type HierarchySearchRow,
} from '@/api/map/hierarchySearch'
import './hierarchy-search.css'

const PAGE_SIZE = HIERARCHY_PAGE_SIZE
const DEBOUNCE_MS = 100

export function HierarchySearchPage() {
  return <HierarchySearchBody />
}

function HierarchySearchBody() {
  const columnsMeta = hierarchyColumnsForAggregate()
  const [tags, setTags] = useState<string[]>([])
  const [draft, setDraft] = useState('')
  const [debouncedDraft, setDebouncedDraft] = useState('')
  const [page, setPage] = useState(1)
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const live = hierarchySearchLive()

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedDraft(draft), DEBOUNCE_MS)
    return () => window.clearTimeout(t)
  }, [draft])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  const suggest = useSearchHierarchyNew(debouncedDraft, open && live, {
    prefetchOnEmpty: true,
  })

  const search = useSearchHierarchyPage(tags, { page, enabled: tags.length > 0 })

  const suggestions = useMemo(
    () =>
      hierarchySearchSuggestions({
        live,
        apiNames: suggest.data,
        draft,
        tags,
      }),
    [suggest.data, tags, draft, live],
  )

  useEffect(() => {
    setActiveIndex(0)
  }, [suggestions, draft])

  const addTag = (name: string) => {
    const clean = name.trim()
    if (!clean) return
    setTags((prev) => (prev.includes(clean) ? prev : [...prev, clean]))
    setDraft('')
    setPage(1)
    setOpen(false)
    inputRef.current?.focus()
  }

  const removeTag = (name: string) => {
    setTags((prev) => prev.filter((t) => t !== name))
    setPage(1)
  }

  const columns = useMemo<Column<HierarchySearchRow>[]>(() => {
    return [
      {
        id: 'index',
        header: '#',
        cell: (_row, index) => (
          <span className="w7-hier-index">{(page - 1) * PAGE_SIZE + index + 1}</span>
        ),
      },
      ...columnsMeta.map((meta) => ({
        id: meta.id,
        header: meta.header,
        cell: (row: HierarchySearchRow) => {
          const name = row[meta.id]
          if (!name) return <span className="w7-hier-empty">—</span>
          return <span className="w7-hier-name">{name}</span>
        },
      })),
    ]
  }, [columnsMeta, page])

  const rows = search.data?.rows ?? []
  const total = search.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const showSuggest =
    open &&
    (suggestions.length > 0 ||
      (live && suggest.isFetching) ||
      (live && suggest.isError) ||
      draft.trim().length > 0)

  return (
    <div className="w7-hier-page">
      <PageHead
        title="Hierarchy Search"
        subtitle="Find anyone in your Aggregate group and see Player → Master → SM → DM → TM."
      />

      {!live ? (
        <Banner tone="info">
          Demo suggestions — sign in against Aggregate-API for live group search.
        </Banner>
      ) : null}

      <Card className="w7-hier-card" flush>
        <div className="w7-hier-search" ref={rootRef}>
          <label className="w7-hier-field" htmlFor="w7-hier-input">
            <Icon name="search" size={16} />
            <div className="w7-hier-chips">
              {tags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  className="w7-hier-chip"
                  onClick={() => removeTag(tag)}
                  title={`Remove ${tag}`}
                >
                  <span>{tag}</span>
                  <span aria-hidden="true">×</span>
                </button>
              ))}
              <input
                id="w7-hier-input"
                ref={inputRef}
                className="w7-hier-input"
                type="text"
                value={draft}
                autoComplete="off"
                spellCheck={false}
                placeholder={
                  tags.length
                    ? 'Add another username'
                    : 'Enter or Select Member Name to Search'
                }
                aria-expanded={showSuggest}
                aria-controls="w7-hier-suggest-list"
                aria-autocomplete="list"
                aria-activedescendant={
                  showSuggest && suggestions[activeIndex]
                    ? `w7-hier-opt-${activeIndex}`
                    : undefined
                }
                onChange={(e) => {
                  setDraft(e.target.value)
                  setOpen(true)
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault()
                    setOpen(true)
                    setActiveIndex((i) => Math.min(i + 1, Math.max(suggestions.length - 1, 0)))
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault()
                    setActiveIndex((i) => Math.max(i - 1, 0))
                  } else if (e.key === 'Enter') {
                    e.preventDefault()
                    const pick = suggestions[activeIndex]
                    if (open && pick) addTag(pick)
                    else if (draft.trim()) addTag(draft)
                  } else if (e.key === 'Backspace' && !draft && tags.length) {
                    removeTag(tags[tags.length - 1]!)
                  } else if (e.key === 'Escape') {
                    setOpen(false)
                  }
                }}
              />
            </div>
            {tags.length ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setTags([])
                  setPage(1)
                  inputRef.current?.focus()
                }}
              >
                Clear
              </Button>
            ) : null}
          </label>

          {showSuggest ? (
            <ul
              id="w7-hier-suggest-list"
              className="w7-hier-suggest"
              role="listbox"
              aria-label="Member suggestions"
            >
              {live && suggest.isFetching && suggestions.length === 0 ? (
                <li className="w7-hier-suggest-status" role="presentation">
                  <Spinner size={14} />
                  <span>Searching group…</span>
                </li>
              ) : null}
              {live && suggest.isError ? (
                <li className="w7-hier-suggest-status" role="presentation">
                  Could not load suggestions — Aggregate API unreachable.
                </li>
              ) : null}
              {suggestions.map((name, index) => (
                <li key={name} role="presentation">
                  <button
                    type="button"
                    id={`w7-hier-opt-${index}`}
                    role="option"
                    aria-selected={index === activeIndex}
                    className="w7-hier-suggest-option"
                    data-active={index === activeIndex ? 'true' : undefined}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      addTag(name)
                    }}
                  >
                    <Icon name="users" size={14} />
                    <HighlightMatch text={name} needle={draft} />
                  </button>
                </li>
              ))}
              {!suggest.isFetching &&
              !suggest.isError &&
              suggestions.length === 0 &&
              draft.trim() ? (
                <li className="w7-hier-suggest-status" role="presentation">
                  No match for “{draft.trim()}”. Press Enter to search that name anyway.
                </li>
              ) : null}
            </ul>
          ) : null}
        </div>

        <p className="w7-hier-hint">
          {tags.length
            ? `${tags.length} selected · searching your Aggregate group`
            : 'Type a name, pick a suggestion, press Enter — results show the chain above each hit.'}
        </p>
      </Card>

      {!tags.length ? (
        <StateBlock icon="search" title="Search your Aggregate group">
          Start typing a username. Results open as Player → Master → SM → DM → TM for each
          match under your linked members.
        </StateBlock>
      ) : search.isLoading ? (
        <StateBlock icon="search" title="Searching…">
          Looking up the hierarchy chain for each username.
        </StateBlock>
      ) : search.isError ? (
        <StateBlock icon="error" title="Search failed" tone="error">
          Aggregate API did not return results. Check the connection and try Clear, then search
          again.
        </StateBlock>
      ) : (
        <Card title={total ? `${total} match${total === 1 ? '' : 'es'}` : undefined} flush>
          <DataTable
            label="Hierarchy search results"
            columns={columns}
            rows={rows}
            rowKey={(r) => r._id}
            loading={search.isFetching && rows.length === 0}
            empty={
              <StateBlock icon="search" title="No matches">
                Those names are not in your Aggregate group, or the spelling does not match
                exactly.
              </StateBlock>
            }
          />
          <Pager
            page={page}
            pageCount={pageCount}
            onChange={setPage}
            total={total}
            pageSize={PAGE_SIZE}
          />
        </Card>
      )}
    </div>
  )
}

function HighlightMatch({ text, needle }: { text: string; needle: string }) {
  const q = needle.trim()
  if (!q) return <span>{text}</span>
  const idx = text.toLowerCase().indexOf(q.toLowerCase())
  if (idx < 0) return <span>{text}</span>
  return (
    <span>
      {text.slice(0, idx)}
      <mark className="w7-hier-mark">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </span>
  )
}
